/**
 * OpenAI Responses input-file helpers for the Excel native ReAct runner.
 *
 * Workbook snapshots are deliberately wire-only: callers append the returned
 * user item to the first request and never store it in canonical conversation
 * history. Later turns use live workbook tools instead of repeatedly sending
 * and cache-writing the full workbook.
 */

/** XLSX media type accepted by OpenAI Responses `input_file` content. */
export const EXCEL_WORKBOOK_INPUT_FILE_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Raw workbook ceiling before base64 expansion.
 *
 * The add-in proxy accepts a 50 MB JSON body. A 32 MiB workbook expands to
 * roughly 42.7 MiB as base64, leaving headroom for tools and conversation
 * history while remaining below OpenAI's per-file ceiling.
 */
export const MAX_EXCEL_WORKBOOK_INPUT_FILE_BYTES = 32 * 1024 * 1024;

/** Ephemeral OpenAI user item that carries one current workbook snapshot. */
export interface ExcelWorkbookInputFileItem {
  /** Responses API role required for mixed input-file content. */
  role: "user";
  /** Ordered workbook attachment and its precedence instruction. */
  content: [
    {
      /** Responses API file-content discriminator. */
      type: "input_file";
      /** Sanitized workbook filename shown to the model. */
      filename: string;
      /** XLSX data URL containing standard base64. */
      file_data: string;
    },
    {
      /** Responses API text-content discriminator. */
      type: "input_text";
      /** Instruction describing snapshot freshness and live-read precedence. */
      text: string;
    },
  ];
}

/**
 * Measure standard base64 without decoding and materialising a second copy.
 *
 * @param base64 - Standard padded base64 returned by workbook export.
 * @returns The decoded payload size in bytes.
 * @throws When the payload is empty or malformed.
 */
export function decodeWorkbookBase64ByteLength(base64: string): number {
  const length = base64.length;
  if (length === 0) {
    throw new Error("Workbook export returned an empty base64 payload.");
  }
  if (length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(base64)) {
    throw new Error("Workbook export returned malformed base64.");
  }

  let padding = 0;
  if (base64.charCodeAt(length - 1) === 61 /* = */) padding += 1;
  if (base64.charCodeAt(length - 2) === 61 /* = */) padding += 1;
  return (length * 3) / 4 - padding;
}

/**
 * Build a safe `.xlsx` filename from the pinned workbook identity.
 *
 * @param workbookName - Optional pinned workbook name or path.
 * @returns A path-free filename with exactly one `.xlsx` extension.
 */
export function workbookInputFileName(workbookName?: string): string {
  const leaf = (workbookName ?? "current-workbook").split(/[\\/]/).pop()?.trim();
  const base = (leaf || "current-workbook").replace(/\.[^.]+$/, "").trim();
  return `${base || "current-workbook"}.xlsx`;
}

/**
 * Create one ephemeral Responses user item containing the current workbook.
 *
 * @param base64 - Standard base64 for the exported workbook.
 * @param workbookName - Optional pinned workbook name used for the attachment.
 * @param maxBytes - Maximum decoded workbook size accepted for attachment.
 * @returns One user item containing the XLSX data URL and precedence guidance.
 * @throws When base64 validation fails or the workbook exceeds `maxBytes`.
 */
export function buildExcelWorkbookInputFileItem(
  base64: string,
  workbookName?: string,
  maxBytes: number = MAX_EXCEL_WORKBOOK_INPUT_FILE_BYTES
): ExcelWorkbookInputFileItem {
  const byteLength = decodeWorkbookBase64ByteLength(base64);
  if (byteLength > maxBytes) {
    throw new Error(
      `Workbook snapshot is ${byteLength} bytes, exceeding the ${maxBytes}-byte input-file limit.`
    );
  }

  return {
    role: "user",
    content: [
      {
        type: "input_file",
        filename: workbookInputFileName(workbookName),
        file_data: `data:${EXCEL_WORKBOOK_INPUT_FILE_MIME_TYPE};base64,${base64}`,
      },
      {
        type: "input_text",
        text:
          "This is the current workbook snapshot for this model turn. " +
          "Use live Excel tool results as authoritative if they differ.",
      },
    ],
  };
}

/**
 * Identify request failures for which removing only the workbook attachment is
 * a safe bounded fallback. Other 400s must remain visible to the caller.
 *
 * @param error - Provider request failure.
 * @returns Whether one retry without the workbook attachment is safe.
 */
export function isExcelWorkbookInputFileError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const record = error as { code?: unknown; message?: unknown };
  const code = typeof record.code === "string" ? record.code : "";
  const message = typeof record.message === "string" ? record.message : String(error);

  if (code === "http_413") return true;
  if (code !== "http_400") return false;

  return /(?:input_file|file_data|xlsx|spreadsheet|base64|file.{0,24}(?:size|limit|large|invalid|unsupported|format))/i.test(
    message
  );
}
